import {
    ComponentChatMessageButtonGroup, ComponentChatMessageGoToBranch,
    ComponentChatMessageImage,
    ComponentChatMessageLink,
    ComponentChatMessageText
} from "../../../../../types/chat";
import {ComponentChatMessageAiText} from "@/components/chat/ComponentChatMessageAiText";
import {GptMessage} from "../../../../../utils/gptAPIClient";

export type ChatComponent = (ComponentChatMessageText | ComponentChatMessageAiText | ComponentChatMessageImage | ComponentChatMessageLink | ComponentChatMessageButtonGroup | ComponentChatMessageGoToBranch);
export type ChatMessageComponent = ComponentChatMessageText | ComponentChatMessageAiText;

/**
 * Checks if the given message is a button group component chat message.
 *
 * @param {any} message - The message to check.
 * @return {boolean} - Returns true if the message is a button group component chat message, otherwise false.
 */
export const isButtonGroup = (message: any): message is ComponentChatMessageButtonGroup => {
    return message && message.__typename === "ComponentChatMessageButtonGroup";
}

/**
 * Check if a given message is a chat message.
 *
 * @param {any} message - The message to be checked.
 * @returns {boolean} - True if the message is a chat message, otherwise false.
 */
export const isChatMessage = (message: any): message is ChatMessageComponent => {
    return message && (message.__typename === "ComponentChatMessageText" || message.__typename === "ComponentChatMessageAiText");
}

/**
 * Transforms a ChatMessageComponent into a GptMessage.
 *
 * @param {ChatMessageComponent} message - The chat message component to transform.
 * @returns {GptMessage} The transformed GptMessage.
 */
export const transformToGptMessage = (message: ChatMessageComponent): GptMessage => {
    const isUser = message.isUser ?? false;
    switch(message.__typename) {
        case "ComponentChatMessageText":
            return {
                content: message.text?.data?.attributes?.translation ?? "",
                role: isUser ? "user" : "assistant",
                isEnd: false,
                src: isUser ? "user" : "cms",
            }
        case "ComponentChatMessageAiText":
            return {
                content: message.text,
                role: isUser ? "user" : "assistant",
                isEnd: false,
                src: isUser ? "user" : "gpt",
            }
        default: {
            return {
                content: "",
                role: "user",
                isEnd: false,
                src: "cms",
            }
        }
    }
}

/**
 * Creates a chat message component.
 *
 * @param {string} message - The text of the chat message.
 * @param {boolean} isUser - Indicates whether the message is from the user.
 * @param {number} duration - The duration of the chat message.
 * @returns {ComponentChatMessageAiText} The created chat message component.
 */
export const createChatMessageComponent = (message: string, isUser: boolean, duration: number = 0): ComponentChatMessageAiText => {
    const now = new Date();
    let staggeredDelay = now.getTime() + 300;

    return {
        __typename: "ComponentChatMessageAiText",
        name: `chatmessage-${staggeredDelay}`,
        text: message,
        parameters: {
            delay: 0.3,
            duration: duration,
            switchBackground: null,
            triggerEffect: null,
        },
        timeToAppear: new Date(staggeredDelay),
        isUser,
        showSendersName: true,
        emoji: "",
    };
}

/**
 * Updates the button group component with a staggered delay.
 *
 * @param {ComponentChatMessageButtonGroup} buttonGroup - The button group component to update.
 * @returns {ComponentChatMessageButtonGroup} - The updated button group component.
 */
export const updateButtonGroup = (buttonGroup: ComponentChatMessageButtonGroup): ComponentChatMessageButtonGroup => {
    const now = new Date();
    let staggeredDelay = now.getTime() + 3000;

    return {
        ...buttonGroup,
        timeToAppear: new Date(staggeredDelay),
    } as ComponentChatMessageButtonGroup
}


/**
 * Removes the last button group from the given chat array and returns it along with the updated chat array.
 * If the last element of the chat array is not a button group, returns null as the button group and the original chat array.
 *
 * @param {ChatComponent[]} chat - The array of chat components to remove the button group from.
 * @returns {{
 *      buttonGroup: ComponentChatMessageButtonGroup | null,
 *      chat: ChatComponent[]
 * }} - An object containing the removed button group and the updated chat array.
 */
export const removeButtonGroupFromChat = (chat: ChatComponent[]): {
    buttonGroup: ComponentChatMessageButtonGroup | null,
    chat: ChatComponent[]
} => {
    let lastComponent = chat.at(-1);
    if (isButtonGroup(lastComponent)) {
        return {
            buttonGroup: lastComponent as ComponentChatMessageButtonGroup,
            chat: chat.slice(0, -1),
        };
    }
    return {
        buttonGroup: null,
        chat,
    }
}